import { Namespace, TFunction } from "i18next";

export type LocalizedError = {
  error_key?: string;
  status?: number;
  details?: string;
  default_message?: string;
};

/**
 * Checks if all elements in the `target` array are present in the `arr` array.
 *
 * @param arr - The string array to check against.
 * @param target - The string array of items to check for.
 * @returns True if all items in `target` are in `arr`, otherwise false.
 */
export const isSubSet = (arr: string[], target: string[]) => target.every((v) => arr.includes(v));

/**
 * Converts a key-boolean map into an object with "data-" prefixed keys.
 * True values become empty strings; false values become undefined.
 *
 * @param map - Record of string keys with truthy/falsy values.
 * @returns Object for use as HTML data attributes.
 *
 * @example
 * createBooleanDataset({ visible: true, disabled: false });
 * // Returns: { 'data-visible': '', 'data-disabled': undefined }
 */
export const createBooleanDataset = (map: Record<string, any>) => {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [`data-${key}`, value ? "" : undefined]));
};

/**
 * Ensures that a given value conforms to the `LocalizedError` shape by safely extracting expected fields.
 *
 * @param value - Any unknown error-like value to be normalized.
 * @returns A `LocalizedError` object with optional `status`, `error_key`, `details`, and `default_message`.
 */
export const ensureError = (value: unknown): LocalizedError => {
  if (typeof value !== "object") {
    return {};
  }

  const response = Object.assign({}, value as { [id: string]: unknown });
  const data = Object.assign({}, response["data"] as { [id: string]: unknown });
  const error: LocalizedError = {};

  if (typeof response["status"] === "number") {
    error["status"] = response["status"];
  }

  if (typeof data["error_key"] === "string") {
    error["error_key"] = data["error_key"];
  }
  if (typeof data["details"] === "string") {
    error["details"] = data["details"];
  }
  if (typeof data["default_message"] === "string") {
    error["default_message"] = data["default_message"];
  }

  return error as LocalizedError;
};

/**
 * Attempts to localize a `LocalizedError` using the i18n translation function with fallback logic.
 *
 * @param t - The translation function from i18next.
 * @param error - The structured `LocalizedError` to be localized.
 * @returns A localized error message string with context-aware handling of `details`.
 */
export const localizeError = <T extends Namespace, P>(t: TFunction<T, P>, error: LocalizedError) => {
  // error message priorities [from key, from status, (unspecified message | supplied default)]
  // when details are provided the context type will be set to details and details will be passed into i18n
  // if there is no translation with details context then it will fallback to no context

  const details = error.details;

  // 1. Try translated error_key
  if (error.error_key) {
    const translated = t(`error.${error.error_key}`, {
      ...(error.details && { details, context: "details" }),
      defaultValue: ""
    });
    if (translated !== "") {
      return translated;
    }
  }

  // 2. Use default_message if present
  if (error.default_message && error.default_message !== "") {
    return error.default_message;
  }

  // 3. Try translated status
  if (error.status) {
    const translated = t(`error.${error.status}`, {
      ...(error.details && { details, context: "details" }),
      defaultValue: ""
    });
    if (translated !== "") {
      return translated;
    }
  }

  // 4. Fallback to unspecified error
  return t("error.unspecified", {
    ...(error.details && { details, context: "details" })
  });
};

export class Countries {
  static raw: {
    name: string;
    code: string;
    bn?: {
      name: string;
      field: string;
    };
    flag: string;
    areaCode: string;
  }[] = [
    {
      name: "Australia",
      code: "AU",
      bn: {
        name: "ABN",
        field: "abn"
      },
      flag: "🇦🇺",
      areaCode: "61"
    },
    {
      name: "Germany",
      code: "DE",
      flag: "🇩🇪",
      areaCode: "49"
    },
    {
      name: "India",
      code: "IN",
      flag: "🇮🇳",
      areaCode: "91"
    },
    {
      name: "New Zealand",
      code: "NZ",
      bn: {
        name: "NZBN",
        field: "business_registration_number"
      },
      flag: "🇳🇿",
      areaCode: "64"
    },
    {
      name: "United Kingdom",
      code: "UK",
      flag: "🇬🇧",
      areaCode: "44"
    },
    {
      name: "United States",
      code: "US",
      flag: "🇺🇸",
      areaCode: "1"
    }
  ];

  static findByCode(code: unknown) {
    return this.raw.find((item) => item.code === code);
  }
}
